import { device } from '@accedo/xdk-core'

type LibraryAdSettings = {
  IFA: string
  IFA_TYPE: string
  LMT: boolean
}

export class VizioSettings implements DeviceSettings {
  public _IFA: string
  public _IFA_TYPE: string
  public _LMT: boolean

  public get ifa(): string {
    return this._IFA
  }

  public get ifaType(): string {
    return this._IFA_TYPE
  }

  public get lmt(): boolean {
    return this._LMT
  }

  public init() {
    const vizioLibLoaded = window?.vizioLibLoaded
    if (!vizioLibLoaded) {
      document.addEventListener('VIZIO_LIBRARY_DID_LOAD', (e) => {
        this.setAdsValues()
      })
    } else {
      this.setAdsValues()
    }
  }

  private setAdsValues() {
    const vizioRef = window?.VIZIO

    vizioRef?.setAdvertiserIDListener((adSettings: LibraryAdSettings) => {
      this._IFA = adSettings.IFA
      this._IFA_TYPE = adSettings.IFA_TYPE
      this._LMT = adSettings.LMT
    })
  }

  public getCaptionState() {
    return new Promise((resolve) => {
      if (!window?.VIZIO) {
        resolve(false)
        return
      }

      try {
        window?.VIZIO?.setClosedCaptionHandler((isCCEnabled) => {
          resolve(!!isCCEnabled)
        })
      } catch (e) {
        console.warn('Unable to get caption state from device: ' + e)
        return false
      }
    })
  }

  public addCaptionChangeEventListener(cb) {
    if (!window?.VIZIO) {
      return cb(false)
    }

    try {
      window?.VIZIO?.setClosedCaptionHandler((isCCEnabled) => {
        cb(isCCEnabled)
      })
    } catch (e) {
      console.warn('Unable to set caption change listener on device: ' + e)
    }
  }

  public removeCaptionChangeEventListener() {}

  public getVoiceState() {
    if (!window?.VIZIO) {
      return false
    }

    try {
      return window?.VIZIO?._ttsEnabled
    } catch (e) {
      console.warn('Unable to get voice state on device: ' + e)
      return false
    }
  }

  public addVoiceChangeEventListener(cb) {
    document.addEventListener(
      'VIZIO_TTS_ENABLED',
      (e) => {
        console.warn(e)
        cb(true)
      },
      false,
    )
    document.addEventListener(
      'VIZIO_TTS_DISABLED',
      function (e) {
        console.warn(e)
        cb(false)
      },
      false,
    )
  }

  public removeVoiceChangeEventListener() {}

  public async getModel(): Promise<DeviceModel> {
    let model = {
      id: null,
      name: 'SmartCast',
      type: 'TV',
      version: null,
    }

    if (!window?.VIZIO) {
      console.warn('Vizio library not ready yet, retrieving model context from userAgeng')

      const ua = navigator.userAgent

      model = {
        ...model,
        id: await device.system.getUniqueId(),
        name: this.extractModelFromUserAgent(ua),
        version: this.extractFirmwareFromUserAgent(ua),
        type: device.system.getDeviceType(),
      }

      return model
    }

    try {
      model = {
        ...model,
        id: await device.system.getUniqueId(),
        name: device.system.getModel(),
        version: window?.VIZIO?.deviceInfo?.firmwareVersion,
        type: device.system.getDeviceType(),
      }
    } catch (e) {
      console.error('There was a problem capturing the model context data. Details:', e)
    }

    return model
  }

  public getOS(): DeviceOS {
    let os = {
      name: '',
      version: '',
    }

    if (!window?.VIZIO) {
      console.warn('Vizio library not ready yet, retrieving os context from userAgent')

      os = {
        name: 'smartcast',
        version: this.extractFirmwareFromUserAgent(navigator.userAgent),
      }

      return os
    }

    try {
      os = {
        name: 'smartcast',
        version: window?.VIZIO?.deviceInfo?.SYSTEM_INFO?.VERSION,
      }
    } catch (e) {
      console.error('There was a problem capturing the os context data. Details:', e)
    }

    return os
  }

  public getAppStoreURL(): string {
    return encodeURI('https://www.vizio.com/en/smart-tv-apps?appname=flosports&appid=vizio.flosports')
  }

  public getAppIDForAds(): string {
    return 'vizio.flosports'
  }

  public getVendor(): string {
    return 'Vizio'
  }

  public getRDID(): string {
    return this.ifa ?? ''
  }

  public isLATEnabled(): boolean {
    return this.lmt ?? true
  }

  private extractModelFromUserAgent(userAgent) {
    const match = userAgent.match(/Model\/([^)\s]+)/)
    return match ? match[1] : null
  }

  private extractFirmwareFromUserAgent(userAgent) {
    const match = userAgent.match(/FW\/([\d.-]+)/)
    return match ? match[1] : null
  }
}
